import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2, Users, FileText, Calendar, Bell, Shield, MapPin, Smartphone } from 'lucide-react';
import { motion } from 'framer-motion';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/e8a02fd5-3c12-4859-8d0e-46bed620e093/images/1193b7a041639f784b7a40c1e423439a99456520d92788df70354c3419c35964.png";
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/e8a02fd5-3c12-4859-8d0e-46bed620e093/images/13acbbf8bab4b92cfe59becedf018e26b24890c349cf6d678a40cdd2a6fd3efb.png";

const Landing = () => {
  return (
    <div className="min-h-screen bg-stone-100">
      {/* Navigation */}
      <nav className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src={LOGO_URL} alt="MyHomeEngineer" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold text-stone-900">MyHomeEngineer</span>
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <a href="#features" className="text-stone-600 hover:text-stone-900 transition-colors">Features</a>
              <a href="#how-it-works" className="text-stone-600 hover:text-stone-900 transition-colors">How It Works</a>
              <Link to="/contact" className="text-stone-600 hover:text-stone-900 transition-colors">Contact</Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/login">
                <Button variant="ghost" className="text-stone-700" data-testid="nav-login-button">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg" data-testid="nav-register-button">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-500/20 text-orange-300 text-sm font-medium mb-6">
                <Shield className="w-4 h-4 mr-2" />
                Trusted by 500+ Homeowners
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Build Your Dream Home
                <span className="block text-orange-400 mt-2">With Complete Digital Control</span>
              </h1>
              <p className="text-lg text-slate-300 mb-8 max-w-xl leading-relaxed">
                Your construction site might be miles away, but you're always in control. 
                MyHomeEngineer brings <strong className="text-white">transparency, authenticity, and real-time tracking</strong> to 
                every brick laid — whether you're across town or across the country.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-8 shadow-lg shadow-orange-500/30" data-testid="hero-get-started-button">
                    Start Your Project
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-slate-500 text-white hover:bg-white/10 rounded-lg px-8" data-testid="hero-contact-button">
                    Talk to Our Team
                  </Button>
                </Link>
              </div>
              
              {/* Trust indicators */}
              <div className="flex items-center gap-6 mt-10 pt-8 border-t border-slate-700">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">500+</div>
                  <div className="text-sm text-slate-400">Projects Delivered</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">98%</div>
                  <div className="text-sm text-slate-400">Client Satisfaction</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">15+</div>
                  <div className="text-sm text-slate-400">Years Experience</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/50">
                <img
                  src={HERO_IMAGE}
                  alt="Digital blueprint with project tracking"
                  className="w-full rounded-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent" />
              </div>
              {/* Floating card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-stone-900">Foundation Complete</div>
                    <div className="text-xs text-stone-500">Updated 2 hours ago</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
              Why Choose MyHomeEngineer?
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              We combine expert engineering with cutting-edge digital tools to give you unprecedented visibility into your construction project
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center p-8"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <MapPin className="w-8 h-8 text-blue-900" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Remote Monitoring</h3>
              <p className="text-stone-600">
                Live from Dubai while building in Kerala? No problem. Track every update, photo, and milestone from anywhere in the world.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center p-8"
            >
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Complete Transparency</h3>
              <p className="text-stone-600">
                Every expense, every material, every decision documented. No hidden costs, no surprises — just honest construction.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center p-8"
            >
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Smartphone className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-3">Digital-First Approach</h3>
              <p className="text-stone-600">
                All documents, approvals, and communications in one secure platform. Access your project data anytime, anywhere.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
              Everything You Need in One Platform
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              From project setup to final handover, manage every aspect of your construction journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FileText,
                title: 'Document Management',
                description: 'Centralized storage for drawings, contracts, and approvals with version control'
              },
              {
                icon: Calendar,
                title: 'Timeline & Milestones',
                description: 'Visual progress tracking with linked payment schedules and completion dates'
              },
              {
                icon: Users,
                title: 'Team Collaboration',
                description: 'Seamless communication between homeowners, engineers, and contractors'
              },
              {
                icon: Bell,
                title: 'Real-time Notifications',
                description: 'Instant alerts for progress updates, approvals needed, and payment reminders'
              },
              {
                icon: CheckCircle2,
                title: 'Material Approvals',
                description: 'Review and approve materials with specifications, costs, and alternatives'
              },
              {
                icon: Shield,
                title: 'Quality Assurance',
                description: 'Photo documentation and engineer sign-offs at every critical stage'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-stone-200 rounded-xl p-6 hover:shadow-lg hover:border-blue-200 transition-all duration-300"
              >
                <feature.icon className="h-10 w-10 text-blue-900 mb-4" />
                <h3 className="text-xl font-semibold text-stone-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-stone-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Getting started with MyHomeEngineer is simple
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Submit Your Requirements', desc: 'Share your land details, budget, and design preferences' },
              { step: '02', title: 'Get Your Plan', desc: 'Our engineers create detailed drawings and project timeline' },
              { step: '03', title: 'Track Progress', desc: 'Monitor every milestone with photos, updates, and approvals' },
              { step: '04', title: 'Move In', desc: 'Receive your completed home with all documentation' }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-5xl font-bold text-blue-100 mb-4">{item.step}</div>
                <h3 className="text-lg font-semibold text-stone-900 mb-2">{item.title}</h3>
                <p className="text-stone-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* User Roles Section */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
              Built for Every Stakeholder
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Tailored experiences for homeowners, engineers, and company admins
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Homeowners',
                description: 'Stay connected to your construction even when you\'re thousands of miles away',
                benefits: ['Real-time progress photos', 'Approve materials & changes', 'Track payments & invoices', 'Direct chat with engineers']
              },
              {
                title: 'Site Engineers',
                description: 'Mobile-first tools for quick updates and seamless documentation',
                benefits: ['Easy photo uploads', 'Milestone tracking', 'Material requests', 'Progress reports']
              },
              {
                title: 'Company Admins',
                description: 'Complete oversight of all projects with powerful management tools',
                benefits: ['Multi-project dashboard', 'Team management', 'Financial tracking', 'Client communications']
              }
            ].map((role, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-stone-200 rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="text-2xl font-bold text-stone-900 mb-3">
                  {role.title}
                </h3>
                <p className="text-stone-600 mb-6">{role.description}</p>
                <ul className="space-y-3">
                  {role.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-stone-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Build with Confidence?
          </h2>
          <p className="text-lg mb-8 text-blue-100">
            Join hundreds of homeowners who trust MyHomeEngineer for transparent, trackable construction
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-8" data-testid="cta-get-started-button">
                Start Your Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-lg px-8" data-testid="cta-contact-button">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-950 text-stone-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={LOGO_URL} alt="MyHomeEngineer" className="w-8 h-8 object-contain" />
                <span className="text-xl font-bold text-white">MyHomeEngineer</span>
              </div>
              <p className="text-sm">
                Building dreams with digital precision. Your trusted partner for residential construction.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800 mt-8 pt-8 text-sm text-center">
            <p>© {new Date().getFullYear()} MyHomeEngineer. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
